import React, { useEffect, useState } from 'react';
import { User, Loader2 } from 'lucide-react';
import { apiFetch } from '../utils/api';

/** Purpose: Executes build initial. Inputs: user. Output: Operation result. */
const buildInitial = (user) => ({
  firstName: user?.firstName || '',
  lastName: user?.lastName || '',
  email: user?.email || '',
  phone: user?.phone || '',
  birthDate: user?.birthDate ? new Date(user.birthDate).toISOString().split('T')[0] : '',
  addressLine1: user?.addressLine1 || '',
  addressLine2: user?.addressLine2 || '',
  city: user?.city || '',
  state: user?.state || '',
  postalCode: user?.postalCode || '',
  currentPassword: ''
});

/** Purpose: Executes { is open, on close, user, on updated }. Inputs: { isOpen, onClose, user, onUpdated }. Output: Operation result. */
export default function UpdateProfileModal({ isOpen, onClose, user, onUpdated }) {
  const [formData, setFormData] = useState(buildInitial(user));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (isOpen) {
      setFormData(buildInitial(user));
      setError('');
      setSuccess('');
    }
  }, [isOpen, user]);

  if (!isOpen) return null;

  const emailChanged = formData.email.trim().toLowerCase() !== (user?.email || '').toLowerCase();

  /** Purpose: Handles change. Inputs: field. Output: Operation result. */
  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  /** Purpose: Handles submit. Inputs: e. Output: Promise with operation result. */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (emailChanged && !formData.currentPassword.trim()) {
      setError('Current password is required to change email.');
      return;
    }

    setSaving(true);
    try {
      const payload = { ...formData };
      if (!payload.currentPassword?.trim()) {
        delete payload.currentPassword;
      }

      const response = await apiFetch('/users/me', {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setError(data.error || data.message || 'Failed to update profile.');
        setSaving(false);
        return;
      }

      setSuccess(
        data.emailVerificationSent
          ? 'Profile updated. Verification email sent.'
          : 'Profile updated.'
      );
      if (onUpdated) onUpdated(data.user);
      setFormData((prev) => ({ ...prev, currentPassword: '' }));
    } catch (err) {
      setError('Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      onClick={(e) => e.target === e.currentTarget && !saving && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="update-profile-title"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-indigo-100">
            <User className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h3 id="update-profile-title" className="text-lg font-semibold text-slate-900">
              Update Profile
            </h3>
            <p className="text-xs text-slate-500">Update your contact and address details.</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-lg">
              {error}
            </div>
          )}
          {success && (
            <div className="text-sm text-green-700 bg-green-50 border border-green-200 px-3 py-2 rounded-lg">
              {success}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">First name</label>
              <input
                value={formData.firstName}
                onChange={handleChange('firstName')}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={saving}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Last name</label>
              <input
                value={formData.lastName}
                onChange={handleChange('lastName')}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={saving}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={handleChange('email')}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={saving}
              />
              {emailChanged && (
                <p className="text-xs text-amber-600 mt-1">Email changes require your current password.</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
              <input
                value={formData.phone}
                onChange={handleChange('phone')}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={saving}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Birth date</label>
            <input
              type="date"
              value={formData.birthDate}
              onChange={handleChange('birthDate')}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              disabled={saving}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Address line 1</label>
            <input
              value={formData.addressLine1}
              onChange={handleChange('addressLine1')}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              disabled={saving}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Address line 2</label>
            <input
              value={formData.addressLine2}
              onChange={handleChange('addressLine2')}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              disabled={saving}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">City</label>
              <input
                value={formData.city}
                onChange={handleChange('city')}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={saving}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">State</label>
              <input
                value={formData.state}
                onChange={handleChange('state')}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={saving}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Postal code</label>
              <input
                value={formData.postalCode}
                onChange={handleChange('postalCode')}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={saving}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Current password</label>
            <input
              type="password"
              value={formData.currentPassword}
              onChange={handleChange('currentPassword')}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              disabled={saving}
              placeholder={emailChanged ? 'Required to change email' : 'Only required if changing email'}
            />
          </div>
        </form>

        <div className="bg-slate-50 px-6 py-4 flex gap-3 justify-end border-t border-slate-200">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-2"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}
